import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { workspaceWithRunTimeTable } from 'service/service';
import moment from 'moment';
import ComputeFlatTableBase from './ComputeFlatTableBase';

const COLUMN_TO_API_MAP: Record<string, string> = {
    compute_cost: 'Compute Cost',
    computeCost: 'Compute Cost',
    dbu: 'DBU',
    total_runtime_hours: 'Run Hours',
    workspace_name: 'Workspace Name'
};

const DEFAULT_COLUMN_NAMES = [
    'Workspace ID', 'Workspace Name', 'Workspace Url', 'Compute Type', 'Cluster Name',
    'Usage Date', 'DBU', 'Compute Cost', 'Run Hours', 'Env'
];

const getColumns = () => [
    { key: 'workspace_id', name: 'Workspace ID', minWidth: 220, formatter: ({ row }: any) => <div title={row.workspace_id}>{row.workspace_id || '--'}</div> },
    { key: 'workspace_name', name: 'Workspace Name', minWidth: 220, formatter: ({ row }: any) => <div title={row.workspace_name}>{row.workspace_name || '--'}</div> },
    {
        key: 'workspace_url',
        name: 'Workspace Url',
        minWidth: 200,
        formatter: ({ row }: any) => {
            const rawUrl = row?.workspace_url;
            const fullUrl = rawUrl && !rawUrl.startsWith('http') ? `https://${rawUrl}` : rawUrl;
            let displayText = '--';
            if (fullUrl) {
                const match = fullUrl.match(/^https?:\/\/([^.]+)/);
                displayText = match ? match[1] : fullUrl;
            }
            return (
                <div title={fullUrl}>
                    {fullUrl ? (
                        <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', textDecoration: 'underline' }}>
                            {displayText}
                        </a>
                    ) : '--'}
                </div>
            );
        }
    },
    { key: 'computetype', name: 'Compute Type', minWidth: 140, formatter: ({ row }: any) => <div>{row.computetype || '--'}</div> },
    { key: 'cluster_name', name: 'Cluster Name', minWidth: 220, formatter: ({ row }: any) => <div title={row.cluster_name}>{row.cluster_name || '--'}</div> },
    { key: 'usage_date', name: 'Usage Date', minWidth: 160, formatter: ({ row }: any) => <div>{row.usage_date ? moment.utc(row.usage_date).format('YYYY-MM-DD') : '--'}</div> },
    { key: 'dbu', name: 'DBU', minWidth: 120, align: 'left', formatter: ({ row }: any) => <div>{typeof row.dbu === 'number' ? row.dbu.toFixed(3) : (row.dbu ?? '--')}</div> },
    {
        key: 'compute_cost', name: 'Compute Cost', minWidth: 160, align: 'left', formatter: ({ row }: any) => {
            const v = row.compute_cost ?? row.computeCost ?? row.cost;
            const num = typeof v === 'string' ? Number(v) : v;
            return <div>{(typeof num === 'number' && Number.isFinite(num)) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 3 }).format(num) : (v ?? '--')}</div>;
        }
    },
    { key: 'total_runtime_hours', name: 'Run Hours', minWidth: 120, align: 'left', formatter: ({ row }: any) => <div>{row.total_runtime_hours ?? '--'}</div> },
    { key: 'env', name: 'Env', minWidth: 100, formatter: ({ row }: any) => <div>{row.env || '--'}</div> }
];

const WorkspaceWithRuntimeTableBase: React.FC = () => {
    const location = useLocation();
    const navigationState = location.state;

    const serviceFuncWrapper = useCallback(async (params: any) => {
        const {
            startDate,
            endDate,
            env,
            sector,
            location: locationName,
            selectedDateInString,
            page,
            pageSize,
            sortField,
            sortOrder,
            filters,
            exportType
        } = params;
        const resp = await workspaceWithRunTimeTable({
            startDate,
            endDate,
            env,
            sector,
            location: locationName,
            selectedDateInString,
            page,
            limit: pageSize,
            filters: filters || [],
            workspaceName: undefined,
            exportType,
            sortField,
            sortOrder
        });

        if (resp?.success && resp.data && !resp.data.docs && resp.data.data) {
            resp.data.docs = resp.data.data;
            resp.data.pagination = {
                totalRecords: resp.data.pagination?.total || resp.data.data.length,
                totalPages: resp.data.pagination?.totalPages || Math.ceil((resp.data.pagination?.total || resp.data.data.length) / pageSize)
            };
        }
        return resp;
    }, []);

    return (
        <ComputeFlatTableBase
            serviceFunc={serviceFuncWrapper}
            pageTitle={navigationState?.dateLabel || "Details"}
            breadcrumbLabel={navigationState?.pageHeader || "Databricks Analysis"}
            breadcrumbPath="/databricksADB/workspace"
            middleBreadcrumb={navigationState?.pageSubHeader ? {
                label: navigationState.pageSubHeader,
                path: '/databricksADB/performance-dashboard',
                state: { ...navigationState }
            } : undefined}
            tableType={navigationState?.detailedTableType || "dbu_count-table"}
            defaultColumnNames={DEFAULT_COLUMN_NAMES}
            getColumns={getColumns}
            columnToApiMap={COLUMN_TO_API_MAP}
            preserveFilterKey="workspace_runtime_filters"
            showQueryBuilder={false}
            getInitialQueryFilters={useCallback(() => [], [])}
            mapQueryRuleToApi={useCallback(() => null, [])}
        />
    );
};

export default WorkspaceWithRuntimeTableBase;
